import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import Messages from "./components/Messages";
import Invites from "./components/Invites";
import AutoMessages from "./components/AutoMessages";
import Settings from "./components/Settings";
import Users from "./components/Users";
import Layout from "./components/Layout";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

const theme = createTheme({
  shape: {
    borderRadius: 16,
  },
  palette: {
    mode: "dark",
    background: {
      default: "#0f1117",
      paper: "#161a23",
    },
    primary: {
      main: "#7dd3fc",
    },
    secondary: {
      main: "#f59e0b",
    },
    text: {
      primary: "#e7ecf5",
      secondary: "#a7b0c2",
    },
    divider: "rgba(255, 255, 255, 0.08)",
  },
  typography: {
    fontFamily: '"Space Grotesk", "Segoe UI", system-ui, sans-serif',
    h4: {
      fontWeight: 600,
      letterSpacing: "-0.02em",
    },
    h5: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
    h6: {
      fontWeight: 600,
      letterSpacing: "-0.01em",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundImage:
            "radial-gradient(1200px 600px at 10% -10%, rgba(125, 211, 252, 0.18), transparent 60%), radial-gradient(900px 500px at 90% 0%, rgba(245, 158, 11, 0.12), transparent 55%)",
          backgroundAttachment: "fixed",
        },
        a: {
          color: "#93c5fd",
          textDecoration: "none",
        },
        "a:hover": {
          color: "#bfdbfe",
          textDecoration: "underline",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage:
            "linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.01))",
          border: "1px solid rgba(255, 255, 255, 0.06)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid rgba(255, 255, 255, 0.06)",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(18, 22, 32, 0.7)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#121622",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        },
        head: {
          color: "#c7d2e5",
          fontWeight: 600,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(255, 255, 255, 0.03)",
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: "4px 8px",
        },
      },
    },
  },
});

// Composant pour protéger les routes
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={{
          "*": { boxSizing: "border-box" },
          "#root": { minHeight: "100vh" },
        }}
      />
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="messages" element={<Messages />} />
              <Route path="auto-messages" element={<AutoMessages />} />
              <Route path="invites" element={<Invites />} />
              <Route path="users" element={<Users />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
